import type { PropsWithChildren, ReactNode } from "react";
import { classNames } from "src/core/utilities/classNames";
import { church } from "./church";
import { churchBackdrop } from "./churchBackdrop";
import { ChurchLogo } from "./ChurchImages";

interface Props {
    /**
     * "two" puts `side` beside the children from `lg`, as the sign-in screen sets its box beside
     * the cards. "one" is a single column, for a screen that asks one thing.
     */
    columns: "one" | "two";

    /** With two columns, what stays in view on the left while the children scroll */
    side?: ReactNode;
}

/**
 * The screens somebody meets before they're signed in: the church's photo as a banner, its logo on
 * a card over the banner's edge, and a page that scrolls as one, so nothing stops a screen down
 * as the split layout it replaced did, its panel being a fixed `h-screen`.
 */
export const ChurchBannerLayout = ({ columns, side, children }: PropsWithChildren<Props>) => (
    <main className="min-h-screen bg-base-200">
        <div
            className="relative h-[150px] md:h-[200px] bg-primary bg-cover bg-center"
            style={{ backgroundImage: churchBackdrop() }}
        >
            <div
                className={classNames("absolute inset-x-0 bottom-0 mx-auto px-4 md:px-10", {
                    "max-w-[35rem] md:px-4": columns === "one",
                    "max-w-6xl": columns === "two",
                })}
            >
                <div className="inline-flex translate-y-7 rounded-2xl bg-base-100 px-3.5 py-2.5 shadow-lg">
                    <ChurchLogo
                        className="dark:invert h-11"
                        fallback={<span className="text-xl font-extrabold tracking-tight">{church.name}</span>}
                    />
                </div>
            </div>
        </div>

        {columns === "two" ? (
            <div className="mx-auto max-w-6xl px-4 pt-11 pb-8 md:px-10 md:pt-12 grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)] lg:gap-7 lg:items-start">
                <div className="grid gap-4 min-w-0 lg:sticky lg:top-6">{side}</div>
                <div className="grid gap-4 min-w-0">{children}</div>
            </div>
        ) : (
            <div className="mx-auto max-w-[35rem] px-4 pt-11 pb-8 md:pt-12 grid gap-4">{children}</div>
        )}
    </main>
);
